import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Spinner, Center, Box } from '@chakra-ui/react'
import { useMyProfile } from '../../api/api_hooks/user/myProfile'
// import { PlanType } from '../../types/subscription'

interface PlanSelectionGuardProps {
  children: React.ReactNode
}

/**
 * Guard that redirects users without a plan to the plan selection page
 * Only applies to new users who haven't selected a plan yet
 */
export default function PlanSelectionGuard({ children }: PlanSelectionGuardProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { data: user, isLoading } = useMyProfile()

  useEffect(() => {
    // Don't redirect if we're already on the plan or module selection pages
    if (location.pathname === '/select-plan' || location.pathname === '/select-modules') {
      return
    }

    const localPlan = localStorage.getItem('userPlan')

    // Clean stale localStorage if backend has no plan
    if (!isLoading && user && !user.plan && localPlan) {
      localStorage.removeItem('userPlan')
      localStorage.removeItem('userModules')
    }

    // Backend is the source of truth
    if (!isLoading && user && !user.plan) {
      navigate('/select-plan', { replace: true })
    }
  }, [user, isLoading, navigate, location.pathname])

  // Only check backend plan, not localStorage
  const hasPlan = user?.plan

  // Show loading spinner while checking user plan
  if (isLoading) {
    return (
      <Center h="100vh">
        <Box>
          <Spinner size="xl" color="brand.purple" thickness="4px" />
        </Box>
      </Center>
    )
  }

  // If user has no plan and we're not on selection pages, show loading
  // (navigation will happen in useEffect)
  if (
    user &&
    !hasPlan &&
    location.pathname !== '/select-plan' &&
    location.pathname !== '/select-modules'
  ) {
    return (
      <Center h="100vh">
        <Box>
          <Spinner size="xl" color="brand.purple" thickness="4px" />
        </Box>
      </Center>
    )
  }

  // User has a plan or is on selection pages, render children
  return <>{children}</>
}
